import i18n from "@/i18n";

export const AUTH_STALE_EVENT = "cognivio:auth-stale";

/**
 * AUTH-WAY-1 (FO1) — the reason map holds KEYS, not copy.
 *
 * It used to hold English string literals, which meant every API failure on the auth path spoke
 * English to a Hebrew reader: this module is not a component, so it never saw `useTranslation`.
 * It now resolves through the app's own i18next instance (`src/i18n.js` default-exports it), which
 * needs no React context and re-reads the active language on every call.
 *
 * AUTH-STRINGS-1 — THE defaultValueS ARE GONE, and with them the last English in this module.
 *
 * Each entry used to carry its former English beside the key, so a key whose bundle value had not
 * landed still rendered a real sentence instead of a raw key. Lane 1's values are on main now and
 * every key below resolves in BOTH bundles — measured, 143 of 143 auth-path keys — so the fallback
 * could no longer do anything except MASK a key that went missing.
 *
 * WHAT REPLACES IT, and why this is a strengthening rather than a removal: `AUTH_ERROR_KEYS` is
 * exported and fenced in apiErrors.i18n.test.js, which asserts every key this module can emit
 * resolves in `en` AND `he`. A defaultValue hid a missing key at runtime; the fence refuses to ship
 * one at all. The guarantee moved from "the reader sees something" to "the key is there".
 */
const SAFE_REASON_KEYS = {
  access_request_already_pending: "authErrors.accessRequestAlreadyPending",
  account_already_exists: "authErrors.accountAlreadyExists",
  account_disabled: "authErrors.accountDisabled",
  account_pending_approval: "authErrors.accountPendingApproval",
  account_rejected: "authErrors.accountRejected",
  duplicate_email: "authErrors.duplicateEmail",
  forbidden_tenant_access: "authErrors.forbiddenTenantAccess",
  invalid_credentials: "authErrors.invalidCredentials",
  // SEC-HARD-1 (PR-2) — the backend password-strength gate's uniform reason codes. Never-alarming,
  // no composition rules named (NIST posture).
  password_too_short: "authErrors.passwordTooShort",
  password_too_long: "authErrors.passwordTooLong",
  invalid_authentication_token: "authErrors.sessionExpired",
  stale_session: "authErrors.sessionExpired",
  token_expired: "authErrors.sessionExpired",
  notification_delivery_failed: "authErrors.notificationDeliveryFailed",
};

/** AUTH-WAY-1 (FO1) — the status fallbacks, same treatment. A 401 with no reason code is the
 *  commonest expiry shape in this codebase (see SESSION_EXPIRED_KEY below), so leaving these as
 *  literals would have left FO3 speaking English however well the reason map was translated. */
const STATUS_FALLBACK_KEYS = {
  400: "authErrors.status400",
  401: "authErrors.sessionExpired",
  403: "authErrors.forbiddenTenantAccess",
  409: "authErrors.status409",
  422: "authErrors.status422",
  429: "authErrors.status429",
};

const SERVER_ERROR_KEY = "authErrors.serverError";
const GENERIC_ERROR_KEY = "authErrors.generic";
const TIMEOUT_KEY = "authErrors.apiTimeout";
const UNREACHABLE_KEY = "authErrors.apiUnreachable";
const MISSING_IDENTITY_KEY = "authErrors.missingIdentityFields";

/**
 * AUTH-WAY-1 (FO3) — the string a stale session gets, whatever the backend said.
 *
 * WHY IT OVERRIDES THE BACKEND'S OWN DETAIL. Neither expiry path sends a reason code. The max-
 * lifetime path raises `detail="Authentication token expired"` (auth_service.validate_token) and
 * the idle path raises `detail="Invalid authentication token"` (auth_middleware._enforce_session_idle),
 * both as PLAIN STRINGS. `pickDetailMessage` therefore used to win, and the reader — in either
 * language — got the raw backend sentence. Those two strings are backend diagnostics, not product
 * copy: they name a token, which is an implementation detail the reader cannot act on.
 *
 * So for an auth-stale 401 the detail is DELIBERATELY not used. This is the one place this module
 * prefers its own copy to the server's, and it is why FO3 is a frontend fix at all.
 */
const SESSION_EXPIRED_KEY = "authErrors.sessionExpired";

/**
 * Every key this module can emit, for the fence in apiErrors.i18n.test.js.
 *
 * DERIVED from the maps, never listed by hand: a hand-kept list is how a fence quietly stops
 * covering the thing it was written for — someone adds a reason code, forgets the list, and the
 * fence still passes while the new key is unguarded.
 */
export const AUTH_ERROR_KEYS = [
  ...new Set([
    ...Object.values(SAFE_REASON_KEYS),
    ...Object.values(STATUS_FALLBACK_KEYS),
    SERVER_ERROR_KEY,
    GENERIC_ERROR_KEY,
    TIMEOUT_KEY,
    UNREACHABLE_KEY,
    MISSING_IDENTITY_KEY,
    SESSION_EXPIRED_KEY,
  ]),
];

/** Resolve a key through the live i18next instance. */
function translate(key) {
  if (!key) return null;
  return i18n.t(key);
}

function pickReasonCode(data) {
  const detail = data?.detail;
  if (typeof data?.reason_code === "string") return data.reason_code;
  if (typeof data?.error_code === "string") return data.error_code;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    if (typeof detail.reason_code === "string") return detail.reason_code;
    if (typeof detail.error_code === "string") return detail.error_code;
  }
  return null;
}

/**
 * ⚑ A DOTTED, LOWER-CAMEL `detail` IS A COPY KEY, NOT A SENTENCE, AND IT MUST NEVER BE SHOWN RAW.
 *
 * F-63 found the first routes that answer this way: `/api/school-reports/*` sets `detail` to a
 * `reports.error.*` KEY (GROWTH-ENGINE, IN-285), so `pickDetailMessage` returned
 * "reports.error.noSections" and a toast put a raw identifier in front of an administrator, which
 * R3 forbids outright.
 *
 * ⚑ AND THE FIX BELONGS HERE RATHER THAN AT THE CALL SITE, which is not a preference: this module
 * OWNS the detail precedence rule, and `apiErrors.detailI18n.test.js` bans every other file in the
 * app from inspecting a detail at all ("the detail-first ternary appears nowhere, however it is
 * spelled"). My first attempt did it in the page and that guard caught it — correctly. Fixing it
 * here also covers every surface a future key-answering route reaches, not just the reports page.
 *
 * Resolution, in order: if the reader's bundles hold the key, that copy is the message; if they do
 * not (the namespace is not shipped yet), the detail is DROPPED so the caller falls through to this
 * module's own bilingual status copy. An untranslated sentence is better than an identifier, and a
 * generic sentence is better than both.
 */
const COPY_KEY_SHAPED = /^[a-z][A-Za-z0-9]*(?:\.[A-Za-z0-9_]+)+$/;

function pickDetailMessage(detail) {
  if (typeof detail === "string" && detail.trim()) {
    const value = detail.trim();
    if (COPY_KEY_SHAPED.test(value) && !value.includes(" ")) {
      // ⚑ `getResource`, NOT `t(key, { defaultValue })`, AND A GUARD IN THIS REPO IS WHY.
      // `pages/AuthPathKeys.test.js` asserts this file's code contains NO `defaultValue` at all:
      // "a defaultValue creeping back is a fallback creeping back", because the English defaults
      // that used to sit beside every key here were removed deliberately. My first version used
      // `{ defaultValue: "" }` -- not an English fallback, the opposite of one -- and the guard
      // refused it. The guard cannot tell the two apart, and widening it to admit mine would
      // retire a real protection to make room for a convenience, so the instrument moved instead.
      //
      // ⚑ AND `getResource` IS THE BETTER ANSWER ANYWAY. It performs no resolution and no
      // fallback, so a key present only in `en` is NOT served to a Hebrew reader -- which is
      // exactly the defect F-60 is about, and `lib/i18nExistsProbe.test.js` measured `t` and
      // `exists` as unable to answer this question without it.
      const lng = i18n.resolvedLanguage || i18n.language;
      const own = i18n.getResource(lng, "translation", value);
      return typeof own === "string" && own.trim() ? own : null;
    }
    return detail;
  }

  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    if (typeof detail.message === "string" && detail.message.trim()) {
      return detail.message;
    }
  }

  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (typeof first === "string" && first.trim()) {
      return first;
    }
    if (first && typeof first.msg === "string" && first.msg.trim()) {
      return first.msg;
    }
  }

  return null;
}

function missingValidationFields(detail) {
  if (!Array.isArray(detail)) {
    return [];
  }

  return detail
    .filter((item) => {
      const type = String(item?.type || "").toLowerCase();
      const message = String(item?.msg || "").toLowerCase();
      return type.includes("missing") || message.includes("field required");
    })
    .map((item) => {
      const loc = Array.isArray(item?.loc) ? item.loc : [];
      return String(loc[loc.length - 1] || "").trim();
    })
    .filter(Boolean);
}

/**
 * DETAIL-I18N-1 (a), ruled by COMMAND 8 Oct 11:33 — WHEN THE ACTIVE LANGUAGE IS NOT ENGLISH, THE
 * TRANSLATED STATUS COPY WINS OVER A PROSE `detail`. English is unchanged.
 *
 * THE DEFECT THIS FIXES, measured before it was ruled: the translated copy existed, had real
 * Hebrew, and was the ELSE BRANCH. `detailMessage || fallbackForStatus(status)` meant the entries
 * above could render only when the server sent NO detail — and for FastAPI, sending one is the
 * normal case. So a 403 carrying prose showed a Hebrew teacher the server's English while
 * `authErrors.forbiddenTenantAccess` sat right there with her language in it. The same 403 with no
 * detail at all rendered correctly. THE PRODUCT WAS MORE CORRECT WHEN THE SERVER SAID LESS.
 *
 * Counted in backend app code: 605 HTTPException sites, 12 of them routed through the one helper
 * that attaches a `reason_code`, and 283 of the 288 403/404 raises carrying prose instead. The real
 * fix is reason codes at the source (registered as REASON-CODES-1 for Track A); this is the
 * mitigation the frontend can make honestly.
 *
 * WHAT IT COSTS, SAID PLAINLY: a Hebrew teacher is told LESS. "Video not found" becomes "something
 * went wrong". For a failure message that is the right direction — understating beats misinforming
 * in a language she did not choose — but it is a real loss of specificity, and option (b) (keep the
 * detail for an allowlist of recognised codes, as `humanizeDetailIfCode` already does on the review
 * progress surface) is registered to recover it.
 *
 * NOT DONE, DELIBERATELY: no "is the detail already Hebrew?" heuristic. The backend sends English
 * today; a script sniff would be cleverness that misfires the day it does not, and the ruling is
 * stated in terms of the ACTIVE LANGUAGE, not of the detail's contents.
 */
const baseLanguage = (language) => String(language || "").toLowerCase().split("-")[0];

/**
 * The bundle every key is guaranteed to resolve in, read from the live instance rather than written
 * here: this module must not hold a second opinion about which language is the fallback.
 */
function fallbackLanguage() {
  const configured = i18n.options?.fallbackLng;
  const first = Array.isArray(configured) ? configured[0] : configured;
  return baseLanguage(first) || "en";
}

/** True when a translated status line should beat the server's prose. */
function prefersTranslatedOverDetail() {
  const active = baseLanguage(i18n.language);
  // An instance with no language yet behaves exactly as before — this fix never makes the unknown
  // case worse, it only changes the case it can reason about.
  if (!active) return false;
  return active !== fallbackLanguage();
}

function fallbackForStatus(status) {
  if (STATUS_FALLBACK_KEYS[status]) return translate(STATUS_FALLBACK_KEYS[status]);
  if (status >= 500) return translate(SERVER_ERROR_KEY);
  return translate(GENERIC_ERROR_KEY);
}

/**
 * AUTH-WAY-1 (FO2) — RESHAPED: a generic line, by design (PRIV-2).
 *
 * There is no lockout branch here, and there must not be one. A locked account is refused with
 * `_raise_auth_error(401, "Invalid email or password", "invalid_credentials")` — the SAME reason
 * code as a wrong password — because saying "this account is locked" is an account-existence and
 * account-state oracle that PRIV-2 deliberately closed (auth_service.login_user).
 *
 * The product answer is therefore ONE generic sentence that covers a wrong credential and too many
 * attempts alike, carried as the VALUE of `authErrors.invalidCredentials` — the key that already
 * renders for this reason. Nothing in the frontend needs to distinguish the two cases, so nothing
 * here tries to: an earlier draft recognised the two lockout reason codes and carried an
 * `isLockout` flag, which was dead code for codes the backend never sends and an invitation to
 * reintroduce the oracle. It is gone. The backend's silence is pinned by a test.
 */
export function normalizeApiError(error) {
  if (error?.normalized) {
    return error.normalized;
  }

  if (!error?.response) {
    const timedOut = error?.code === "ECONNABORTED";
    return {
      status: 0,
      reason_code: timedOut ? "api_timeout" : "api_unreachable",
      message: translate(timedOut ? TIMEOUT_KEY : UNREACHABLE_KEY),
      action: "open_app_domain",
      isNetworkError: true,
    };
  }

  const { status, data = {} } = error.response;
  const reasonCode = pickReasonCode(data);
  const missingFields = missingValidationFields(data.detail);
  const requiredAccessFieldsMissing = ["email", "password", "name"].some((field) =>
    missingFields.includes(field)
  );
  const detailMessage = pickDetailMessage(data.detail);

  // Computed BEFORE the message, because an auth-stale 401 changes which message wins (FO3).
  const isAuthStale =
    status === 401 &&
    !["invalid_credentials", "account_pending_approval", "account_rejected"].includes(
      reasonCode || ""
    );

  const structuredMessage = reasonCode ? translate(SAFE_REASON_KEYS[reasonCode]) : null;

  // DETAIL-I18N-1 (a): the only thing that changes is which of these two comes first, and it
  // changes only for a non-English reader. A recognised reason code still outranks both, because a
  // code carries MORE meaning than a status line, not less.
  const translatedFallback = fallbackForStatus(status);
  const detailOutranksFallback = !prefersTranslatedOverDetail();

  const message = isAuthStale
    ? // FO3 — never the backend's raw token diagnostic. See SESSION_EXPIRED_KEY.
      structuredMessage || translate(SESSION_EXPIRED_KEY)
    : structuredMessage ||
      (status === 422 && requiredAccessFieldsMissing ? translate(MISSING_IDENTITY_KEY) : null) ||
      (detailOutranksFallback
        ? detailMessage || translatedFallback
        : translatedFallback || detailMessage);

  return {
    status,
    reason_code: reasonCode,
    action: data.action || data.detail?.action || null,
    message,
    isAuthStale,
  };
}

export function attachNormalizedApiError(error) {
  const normalized = normalizeApiError(error);
  // eslint-disable-next-line no-param-reassign
  error.normalized = normalized;
  return error;
}

export function getNormalizedApiErrorMessage(error, fallback) {
  return normalizeApiError(error).message || fallback;
}

/**
 * DETAIL-I18N-1 (a) — the message for a surface THAT HAS ITS OWN SENTENCE for this failure.
 *
 * Forty of the forty-five sites that show a server `detail` to a teacher never reach
 * `normalizeApiError` at all. They are written:
 *
 *     toast.error(typeof detail === "string" ? detail : t("someKey"));
 *
 * so their own translated sentence is the ELSE BRANCH and renders only when the server stays
 * silent. That is the defect, forty times over, and the central precedence fix above does not touch
 * it: `master-admin/consoleError.js` is the only caller the normaliser has.
 *
 * WHY THIS IS NOT `getNormalizedApiErrorMessage`, which would have been the obvious reuse. That
 * helper's message is almost never falsy — `fallbackForStatus` always returns something — so the
 * `fallback` argument would effectively never render, and every one of these surfaces would trade
 * its own specific line ("That cohort could not be created") for a generic status line ("Something
 * went wrong"). In ENGLISH that is a straight regression, and in Hebrew it throws away specificity
 * the surface already had translated.
 *
 * SO THE TRANSLATED COPY THAT WINS HERE IS THE SURFACE'S OWN, not the generic status line — which
 * is strictly better than the option (a) I wrote up, and it answers the cost I reported with it. I
 * said a Hebrew teacher would be told less; at these forty sites she is told exactly as much as an
 * English reader, in her own language. The generic line remains the last resort, for a surface that
 * passes no sentence of its own.
 *
 * Precedence, which is the same rule as above and deliberately so:
 *   1. a recognised reason_code — it carries more meaning than any status line;
 *   2. the server's prose, for an English reader only;
 *   3. the surface's own translated sentence;
 *   4. the generic status line, if the surface gave none.
 */
export function apiErrorMessage(error, ownFallback) {
  if (!error?.response) {
    // A timeout or an unreachable API is not this surface's failure to describe.
    return normalizeApiError(error).message || ownFallback;
  }

  const { status, data = {} } = error.response;
  const reasonCode = pickReasonCode(data);
  const structured = reasonCode ? translate(SAFE_REASON_KEYS[reasonCode]) : null;
  if (structured) return structured;

  const detailMessage = pickDetailMessage(data.detail);
  if (detailMessage && !prefersTranslatedOverDetail()) return detailMessage;

  return ownFallback || fallbackForStatus(status);
}
